import React from 'react';
import capitalize from 'lodash/capitalize';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import Button from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import { Flex } from '@atlaskit/primitives';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { EntitlementIcon, SummaryPanelContainer, SummaryPanelRow } from '@css-xp/base';
import { BillingFrequency, EntitlementDetails, PricingPlan } from '@css-xp/graphtypes/src/generated-types-new';
import { PricingType } from '@atlassian/cssxp-types-promotion';
import { NoData } from '@css-xp/ppc-common';
import { pluralize } from '@css-xp/ppc-utils';
import { FormData } from './change-subscription-pane';
import { getPricingPlanDescription } from './utils/change-subscription-pane-utils';
import './plan-summary.scss';

interface Props {
  entitlement: EntitlementDetails;
  formState: FormData;
  isExistingAnnualPlanForJCA?: boolean;
  heading?: string;
  setCurrentProgressIndex: (value: number) => void;
  isSkipTrialChecked?: boolean;
  isFetchingPricingPlans?: boolean;
  defaultPricingPlan?: PricingPlan;
  isProductIconRequired?: boolean;
  infoMessage?: React.ReactNode;
  isDesignIteration1Enabled?: boolean;
  nextBillDate?: string;
}

export const NewPlanSummary = ({
  entitlement,
  formState,
  isExistingAnnualPlanForJCA,
  heading,
  setCurrentProgressIndex,
  isSkipTrialChecked,
  defaultPricingPlan,
  isFetchingPricingPlans,
  isProductIconRequired,
  infoMessage,
  isDesignIteration1Enabled,
  nextBillDate,
}: Props) => {
  const currentBillingCycle = entitlement?.pricingPlan?.billingFrequency ?? BillingFrequency.MONTHLY;
  const newBillingCycle = formState?.billingFrequency ?? currentBillingCycle;
  const isMonthlySubscription = currentBillingCycle === BillingFrequency.MONTHLY;

  const getProductName = () => {
    const { productName, productId } = entitlement;
    if (isProductIconRequired && entitlement?.productId && productName) {
      return (
        <Flex gap="space.100">
          <EntitlementIcon productId={productId} className="avatar medium" isCloud />
          <p className="text-ellipsis">{productName}</p>
        </Flex>
      );
    }

    return <p>{defaultTo(productName, 'Unnamed product')}</p>;
  };

  const renderHeading = () => {
    if (isDesignIteration1Enabled) {
      return heading && <h4>{heading}</h4>;
    }
    return (
      <>
        {heading && <h4>{heading}</h4>}
        <Button appearance="link" onClick={() => setCurrentProgressIndex(0)}>
          Change
        </Button>
      </>
    );
  };

  const renderNewPlan = () => {
    if (isDesignIteration1Enabled) {
      return null;
    }
    return <SummaryPanelRow label="New plan" value={<p>{formState?.newPlan?.label ?? '-'}</p>} />;
  };

  const renderTrial = () => {
    if (isDesignIteration1Enabled) {
      return null;
    }
    return (
      <SummaryPanelRow label="Trial" value={<p>{isSkipTrialChecked || !!formState?.skipTrial ? 'No' : 'Yes'}</p>} />
    );
  };

  const renderNextBillDate = () => {
    if (!isDesignIteration1Enabled) {
      return null;
    }

    if (formState?.newPlan?.label?.toUpperCase() === PricingType.FREE) {
      return null;
    }

    return <SummaryPanelRow label="Next bill date" value={<p>{nextBillDate || <NoData />}</p>} />;
  };

  return (
    <div className="plan-summary new-plan-summary">
      <SummaryPanelContainer isHeaderContentHidden={true}>
        <div className="heading-container">{renderHeading()}</div>
        {infoMessage && <SectionMessage appearance="information">{infoMessage}</SectionMessage>}
        <SummaryPanelRow label="Product name" value={getProductName()} />
        <SummaryPanelRow label="Entitlement number" value={<p>{entitlement?.entitlementNumber}</p>} />
        <SummaryPanelRow label="Billing cycle" value={<p>{capitalize(newBillingCycle)}</p>} />
        {renderNextBillDate()}
        {renderNewPlan()}
        {renderTrial()}
        <SummaryPanelRow
          label="New pricing plan"
          value={
            <p>
              {isFetchingPricingPlans ? (
                <SkeletonLoaderWrapper>
                  <SkeletonLoader />
                </SkeletonLoaderWrapper>
              ) : (
                getPricingPlanDescription(defaultPricingPlan)
              )}
            </p>
          }
        />
        {isMonthlySubscription && (
          <SummaryPanelRow
            label={isDesignIteration1Enabled ? 'Quantity' : 'Active users'}
            value={<p>{isNil(entitlement?.activeUsers) ? <NoData /> : pluralize(entitlement?.activeUsers, 'user')}</p>}
          />
        )}
        {isExistingAnnualPlanForJCA && (
          <SummaryPanelRow label={'User tier'} value={<p>{formState?.userTier?.label ?? '-'}</p>} />
        )}
      </SummaryPanelContainer>
    </div>
  );
};
